import React, { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Droplet } from "lucide-react";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Switch from "../common/Switch";
import { updateWatermarkEventDetails } from "../../services/eventDetailService";
import { color, radius } from "../../styles/theme";

/**
 * Watermark Event Details — ported from mobile's watermark-event-details
 * screen (src/Screens/watermark-event-details). Toggles whether the event
 * name/date get overlaid as text next to the watermark image on shared
 * photos. Opened from Event Details' Settings tab and from @Room Settings.
 *
 * Mobile reads/writes these two booleans straight off the generic event
 * object (no dedicated GET) — same here: initial values come from the
 * already-loaded `event` prop, saved via the same generic event-update PUT.
 */
const WatermarkEventDetailsModal = ({ event, eventId, onClose, onUpdated }) => {
  const { t } = useTranslation(["eventDetails", "common"]);
  const initial = {
    showName: Boolean(event?.ShowWatermarkEventName),
    showTime: Boolean(event?.ShowWatermarkEventTime),
  };
  const [showName, setShowName] = useState(initial.showName);
  const [showTime, setShowTime] = useState(initial.showTime);
  const [saving, setSaving] = useState(false);

  const hasChanges = showName !== initial.showName || showTime !== initial.showTime;

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateWatermarkEventDetails(eventId, {
        ShowWatermarkEventName: showName,
        ShowWatermarkEventTime: showTime,
      });
      toast.success(res.data?.Message || t("watermarkEventDetailsModal.updateSuccess"));
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("watermarkEventDetailsModal.updateError"));
    } finally {
      setSaving(false);
    }
  };

  const previewText = !showName && !showTime
    ? t("watermarkEventDetailsModal.hiddenPreview")
    : [showName ? (event?.Name || t("watermarkEventDetailsModal.defaultEventTitle")) : null, showTime ? formatPreviewDate(event) : null]
        .filter(Boolean)
        .join(" · ");

  return (
    <Modal
      title={t("watermarkEventDetailsModal.title")}
      subtitle={t("watermarkEventDetailsModal.subtitle")}
      onClose={onClose}
      busy={saving}
      maxWidth={420}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>{t("common:actions.cancel")}</Button>
          <Button onClick={handleSave} disabled={saving || !hasChanges}>{saving ? t("watermarkEventDetailsModal.saving") : t("watermarkEventDetailsModal.saveChanges")}</Button>
        </>
      }
    >
      <div style={s.preview}>
        <span style={s.previewIcon}><Droplet size={16} strokeWidth={2} /></span>
        <span style={s.previewText}>{previewText}</span>
      </div>

      <div style={s.switchRow}>
        <span>
          <span style={s.fieldLabel}>{t("watermarkEventDetailsModal.showTitle.label")}</span>
          <span style={s.fieldSub}>{t("watermarkEventDetailsModal.showTitle.description")}</span>
        </span>
        <Switch checked={showName} onChange={(e) => setShowName(e.target.checked)} disabled={saving} />
      </div>

      <div style={{ ...s.switchRow, borderBottom: "none" }}>
        <span>
          <span style={s.fieldLabel}>{t("watermarkEventDetailsModal.showDate.label")}</span>
          <span style={s.fieldSub}>{t("watermarkEventDetailsModal.showDate.description")}</span>
        </span>
        <Switch checked={showTime} onChange={(e) => setShowTime(e.target.checked)} disabled={saving} />
      </div>
    </Modal>
  );
};

const formatPreviewDate = (event) => {
  if (!event?.StartDateTime) return "dd.mm.yyyy";
  const d = new Date(event.StartDateTime);
  const pad2 = (n) => String(n).padStart(2, "0");
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const s = {
  preview: {
    display: "flex", alignItems: "center", gap: 10, background: color.gray50, border: `1px solid ${color.border}`,
    borderRadius: radius.sm, padding: "12px 14px", marginBottom: 16,
  },
  previewIcon: {
    width: 28, height: 28, borderRadius: radius.sm, background: color.primary50, color: color.primary600,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  previewText: { fontSize: 12.5, color: color.gray700, fontWeight: 600 },
  switchRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
    padding: "12px 2px", borderBottom: `1px solid ${color.gray100}`,
  },
  fieldLabel: { display: "block", fontWeight: 600, fontSize: 13.5, color: color.gray800 },
  fieldSub: { display: "block", fontSize: 11.5, color: color.textMuted, marginTop: 2 },
};

export default WatermarkEventDetailsModal;
